import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { ScrollSmoother } from "gsap/ScrollSmoother";
import { isMobile } from "detect-touch-device";

let smoother;

class CustomAnimation {
  smoother;

  init() {
    gsap.registerPlugin(ScrollTrigger, ScrollSmoother);

    this.initSmoothScroll();
    this.initHeaderAnimation();
    this.initTextAnimation();
    this.initSectionAnimation();
    this.initImageAnimation();
    this.initSpacerAnimation();
    this.initFooterAnimation();
    this.initModalListeners(); // Modal Scroll-Locking initialisieren
  }

  initSmoothScroll = () => {
    if (!isMobile) {
      smoother = ScrollSmoother.create({
        wrapper: ".page",
        content: ".scroll",
        smooth: 1.6,
        effects: true,
        smoothTouch: 0,
        normalizeScroll: true,
        speed: 1,
      });
    }
  };

  getSmoother = () => {
    return smoother;
  };

  /**
   * **Modal Scroll-Locking Logik**
   * Blockiert ScrollSmoother und ScrollTrigger, wenn das Modal geöffnet ist.
   */
  initModalListeners = () => {

    const disableScroll = () => {
      if (smoother) smoother.paused(true); // ScrollSmoother pausieren
      ScrollTrigger.getAll().forEach((trigger) => trigger.disable()); // ScrollTrigger deaktivieren
      document.body.style.overflow = "hidden"; // Body-Scroll deaktivieren
    };

    const enableScroll = () => {
      if (smoother) smoother.paused(false); // ScrollSmoother reaktivieren
      ScrollTrigger.getAll().forEach((trigger) => trigger.enable()); // ScrollTrigger reaktivieren
      document.body.style.overflow = ""; // Body-Scroll aktivieren
    };

    // Modal-Selektoren
    // Alle Modale auswählen
    const modals = document.querySelectorAll(".modal"); // Alle Modale auf der Seite

    // Event-Listener an jedes Modal binden
    if (modals.length > 0) {
      modals.forEach((modal) => {
        modal.addEventListener("show.bs.modal", disableScroll);
        modal.addEventListener("hidden.bs.modal", enableScroll);
      });
    }

  };

  initHeaderAnimation = () => {
    gsap.to(".header_animation", {
      scrollTrigger: {
        trigger: ".header",
        start: "top 0",
        end: "bottom top",
        markers: false,
        scrub: true,
      },
      scale: 1.1,
      y: -100,
    });
  };

  initTextAnimation = () => {
    const yAnimation = gsap.utils.toArray(".y_animation");
    yAnimation.forEach((element) => {
      gsap.from(element, {
        y: 20,
        scrollTrigger: {
          trigger: element,
          start: "top bottom",
          end: "top 80%",
          scrub: true,
          markers: false,
        },
      });
    });

    const scaleAnimation = gsap.utils.toArray(".scale_animation");
    scaleAnimation.forEach((element) => {
      gsap.from(element, {
        scale: 0.9,
        scrollTrigger: {
          trigger: element,
          start: "top bottom",
          end: "top 80%",
          scrub: true,
          markers: false,
        },
      });
    });
  };

  initSectionAnimation = () => {
    const sectionAnimation = gsap.utils.toArray(".section_animation");
    sectionAnimation.forEach((element) => {
      gsap.from(element, {
        y: 100,
        scrollTrigger: {
          trigger: element,
          start: "top bottom",
          end: "top 50%",
          scrub: true,
          markers: false,
        },
      });
    });
  };

  initImageAnimation = () => {

    let mm = gsap.matchMedia();

    mm.add("(min-width: 800px)", () => {
      // contact > image-top
      const imageTop = gsap.utils.toArray('.image-top');
      imageTop.forEach((element) => {
        gsap.from(element, {
          scrollTrigger: {
            trigger: element,
            start: 'top 100%',
            end: 'top 50%',
            markers: false,
            scrub: 1
          },
          y: 100,
          //yPercent: 10,
          alpha: 1
        });
        ScrollTrigger.refresh();
      });
    });

    mm.add("(max-width: 799px)", () => {
      /// contact > image-top
      const imageTop = gsap.utils.toArray('.image-top');
      imageTop.forEach((element) => {
        gsap.from(element, {
          scrollTrigger: {
            trigger: element,
            start: 'top 100%',
            end: 'top 50%',
            markers: false,
            scrub: 1
          },
          y: 40,
          //yPercent: 10,
          alpha: 1
        });
        ScrollTrigger.refresh();
      });
    });





    // team_zitat_image
    const teamZitatImage = gsap.utils.toArray('.team_zitat_image');
    teamZitatImage.forEach((element) => {
      gsap.from(element, {
        y: 100, // 100 Bewegung nach oben (langsamer als der Scroll)
        ease: "none",
        scrollTrigger: {
          trigger: element,        // Das Bild triggert die Animation
          start: "top bottom",   // Startpunkt: Bild kommt in den Viewport
          end: "bottom top",     // Endpunkt: Bild verlässt den Viewport
          scrub: true,           // Synchronisierung mit dem Scrollen
          markers: false,        // Debug: Marker deaktivieren
        },
      });
    });

    // team_zitat_holder
    const teamZitatHolder = gsap.utils.toArray('.team_zitat_holder');
    teamZitatHolder.forEach((element) => {
      gsap.fromTo(
        element,
        { y: -50, autoAlpha: 1 }, // -200 Start: Leichte Bewegung nach unten und unsichtbar
        {
          y: 0,               // Endzustand: Ursprungsposition
          autoAlpha: 1,       // Endzustand: Sichtbar
          duration: 1,        // Optionale Zeit für weichere Bewegung
          ease: "power1.out", // Flüssiger Transition
          scrollTrigger: {
            trigger: element,        // Text wird als Trigger verwendet
            start: "top 100%",     // Startpunkt: Text kommt in den Viewport
            end: "bottom 0%",       // Optional: Endpunkt für die Animation
            scrub: true,          // Synchronisierung mit Scroll
            markers: false,       // Debug: Marker deaktivieren
          },
        }
      );
      ScrollTrigger.refresh();
    });

    // background_image_animation
    const backgroundImage = gsap.utils.toArray('.background_image_animation');
    backgroundImage.forEach((element) => {
      gsap.from(element, {
        scrollTrigger: {
          trigger: element,
          start: 'top 100%',
          end: 'bottom 10%',
          markers: false,
          scrub: 1
        },
        y: 100,
        scale: .9,
        alpha: 1
      });
      ScrollTrigger.refresh();
    });

    // background_image_parallax
    gsap.utils.toArray(".background_image_parallax").forEach((parallaxSection) => {
      const image = parallaxSection.querySelector("img");

      gsap.fromTo(image, {
          yPercent: -20,
      },
      {
        yPercent: 20,
        ease: "none",
        scrollTrigger: {
          trigger: parallaxSection,
          start: "top bottom", // Startpunkt
          end: "bottom top",   // Endpunkt
          scrub: true,          // sanft mit Scrollen verbunden
          markers: false
        }
      });
    });



// Aktualisiere ScrollTrigger nach der Einrichtung
    ScrollTrigger.refresh();

  }

  initSpacerAnimation = () => {
    const serviceSpacer = gsap.utils.toArray('.spacer_line_holder');
    serviceSpacer.forEach((element) => {
      gsap.from(element, {
          scrollTrigger: {
            trigger: element,
            start: 'top 100%',
            end: 'top 80%',
            markers: false,
            scrub: 1
          },
          width: 1
        });

    });
  }

  initFooterAnimation = () => {

    gsap.from('.footer', {
      scrollTrigger: {
        trigger: '.footer', // Footer-Element als Auslöser (Trigger)
        start: 'top bottom', // Start der Animation, wenn der Footer im Viewport auftaucht
        end: 'bottom bottom', // Ende der Animation, wenn der Footer den unteren Viewport erreicht
        scrub: true, // Sanfter Übergang mit Scrollen synchronisiert
      },
      y: -100, // Footer bewegt sich um 100px nach oben
      ease: 'none', // Keine zusätzliche Animation (Konstante Bewegung)
    });
  }

  onResize = () => {
    jQuery(window)
      .resize(function () {

      })
  };
}

export default CustomAnimation;
